import React, { useLayoutEffect, useState, useMemo } from 'react';
import { GoogleMap, Marker } from '@react-google-maps/api';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Field } from 'formik';
import '../style.css';

export default function MapSection({
  values,
  errors,
  touched,
  handleChange,
  setFieldValue,
  stateRef,
  mapContainerStyle,
  mapOptions,
  currentLocation,
  shopMarkerPosition
}) {
  const [mapCenter, setMapCenter] = useState(shopMarkerPosition || currentLocation);

  useLayoutEffect(() => {
    const initialPosition = shopMarkerPosition || currentLocation;
    setMapCenter(initialPosition);
    stateRef.current.markerPosition = initialPosition;
    setFieldValue('markerPosition', initialPosition);
  }, [currentLocation, shopMarkerPosition, setFieldValue, stateRef]);

  const cleanMapOptions = useMemo(() => ({
    disableDefaultUI: true,
    zoomControl: !stateRef.current.setupCompleted,
    mapTypeControl: false,
    streetViewControl: false,
    fullscreenControl: false,
    rotateControl: false,
    styles: [
      {
        featureType: "poi",
        elementType: "labels",
        stylers: [{ visibility: "off" }]
      },
      {
        featureType: "transit",
        elementType: "labels",
        stylers: [{ visibility: "off" }]
      }
    ]
  }), [stateRef]);

  return (
    <div className="form-section">
      <h3>Location</h3>
      <small className="text-500 block mb-2">
        Drag and drop the pin to set your exact shop location
      </small>

      <div className="map-container">
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          center={stateRef.current.markerPosition || mapCenter}
          zoom={15}
          options={cleanMapOptions}
          onClick={(e) => {
            const newPosition = {
              lat: e.latLng.lat(),
              lng: e.latLng.lng()
            };
            stateRef.current.markerPosition = newPosition;
            setFieldValue('markerPosition', newPosition);
          }}
        >
          {stateRef.current.markerPosition && (
            <Marker
              position={stateRef.current.markerPosition}
              draggable={true}
              onDragEnd={(e) => {
                const newPosition = {
                  lat: e.latLng.lat(),
                  lng: e.latLng.lng()
                };
                stateRef.current.markerPosition = newPosition;
                setFieldValue('markerPosition', newPosition);
              }}
            />
          )}
        </GoogleMap>
      </div>

      <Field name="storeAddress">
        {({ field, meta }) => (
          <div className="field">
            <label htmlFor="storeAddress">Address</label>
            <InputText
              id="storeAddress"
              {...field}
              onChange={(e) => {
                handleChange(e);
                stateRef.current.storeAddress = e.target.value;
              }}
              className={meta.touched && meta.error ? 'p-invalid' : ''}
              placeholder="Building, Floor, Unit, Street, City (e.g. SM Megamall, 3rd Floor, Unit 325)"
            />
            {meta.touched && meta.error ? (
              <small className="p-error">{meta.error}</small>
            ) : (
              <small className="text-500">
                Include specific details (building, floor, unit) to help customers find your store
              </small>
            )}
          </div>
        )}
      </Field>

      <Field name="directions">
        {({ field, meta }) => (
          <div className="field">
            <label htmlFor="directions">Directions & Landmarks (150 characters max)</label>
            <InputTextarea
              id="directions"
              {...field}
              onChange={(e) => {
                handleChange(e);
                stateRef.current.directions = e.target.value;
              }}
              rows={3}
              maxLength={150}
              className={meta.touched && meta.error ? 'p-invalid' : ''}
              placeholder="Nearby landmarks or specific directions (e.g. Beside Starbucks, near the main entrance)"
            />
            {meta.touched && meta.error ? (
              <small className="p-error">{meta.error}</small>
            ) : (
              <small className="text-500">
                Help customers find your location with additional directions
              </small>
            )}
          </div>
        )}
      </Field>
    </div>
  );
}